// M6 – Lịch sử giao dịch (6.6): thu / thu nợ / hoàn theo phương thức, thẻ tổng hợp, chốt ca
import { useMemo, useState } from 'react';
import { DatePicker, Input, Modal, Select, Table, Tabs, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { Link } from 'react-router-dom';
import { Calculator, Download } from 'lucide-react';
import { BRANCHES, METHOD_LABEL, METHODS, branchName, type PaymentMethod, type Transaction, type TxnKind } from './data';
import usePosStore, { emptyByMethod } from './store';
import { branchOptions, exportExcel, fmtDateTime, money, usePosAuth } from './utils';
import PosShell from './components/PosShell';
import MoneyInput from './components/MoneyInput';

const KIND_LABEL: Record<TxnKind, string> = { payment: 'Thu', debt_collection: 'Thu nợ', refund: 'Hoàn' };
const KIND_CLS: Record<TxnKind, string> = { payment: 'bg-green-50 text-green-700', debt_collection: 'bg-sky-50 text-sky-700', refund: 'bg-red-50 text-red-600' };

export default function PosTransactions() {
  const transactions = usePosStore(s => s.transactions);
  const orders = usePosStore(s => s.orders);
  const shiftCloses = usePosStore(s => s.shiftCloses);
  const closeShift = usePosStore(s => s.closeShift);
  const workingBranch = usePosStore(s => s.workingBranch);
  const { can, role, userName } = usePosAuth();

  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().startOf('day'), dayjs().endOf('day')]);
  const [branches, setBranches] = useState<string[]>(role === 'cashier' ? [workingBranch] : []);
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [kinds, setKinds] = useState<TxnKind[]>([]);
  const [cashiers, setCashiers] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const [shiftOpen, setShiftOpen] = useState(false);

  const customerOf = useMemo(() => new Map(orders.map(o => [o.id, o.customerName])), [orders]);
  const allCashiers = useMemo(() => Array.from(new Set(transactions.map(t => t.cashier))).sort(), [transactions]);

  const data = useMemo(() => {
    const nq = q.trim().toLowerCase();
    return transactions.filter(t => {
      const d = dayjs(t.at);
      return !d.isBefore(range[0]) && !d.isAfter(range[1]) &&
        (!branches.length || branches.includes(t.branchId)) && (!methods.length || methods.includes(t.method)) &&
        (!kinds.length || kinds.includes(t.kind)) && (!cashiers.length || cashiers.includes(t.cashier)) &&
        (!nq || t.id.toLowerCase().includes(nq) || t.orderId.toLowerCase().includes(nq) || t.txnRef.toLowerCase().includes(nq) || (customerOf.get(t.orderId) || '').toLowerCase().includes(nq));
    });
  }, [transactions, range, branches, methods, kinds, cashiers, q, customerOf]);

  // T1: thẻ tổng hợp theo đúng bộ lọc; Thực thu = Σ Thu − Σ Hoàn
  const summary = useMemo(() => METHODS.map(m => {
    const rows = data.filter(t => t.method === m);
    const income = rows.filter(t => t.amount > 0).reduce((s, t) => s + t.amount, 0);
    const refund = rows.filter(t => t.amount < 0).reduce((s, t) => s - t.amount, 0);
    return { method: m, income, refund, net: income - refund, count: rows.length };
  }), [data]);
  const grand = summary.reduce((acc, s) => ({ income: acc.income + s.income, refund: acc.refund + s.refund }), { income: 0, refund: 0 });

  const columns: ColumnsType<Transaction> = [
    { key: 'id', title: 'Mã GD', dataIndex: 'id', width: 110, render: v => <span className="font-mono text-[13px]">{v}</span> },
    { key: 'at', title: 'Thời gian', width: 140, render: (_, t) => fmtDateTime(t.at), sorter: (a, b) => a.at.localeCompare(b.at), defaultSortOrder: 'descend' },
    { key: 'kind', title: 'Loại', width: 90, render: (_, t) => <span className={`rounded px-2 py-0.5 text-xs font-semibold ${KIND_CLS[t.kind]}`}>{KIND_LABEL[t.kind]}</span> },
    { key: 'order', title: 'Mã đơn', width: 200, render: (_, t) => <Link to={`/pos/orders?id=${t.orderId}&from=${dayjs(t.at).subtract(60, 'day').format('YYYY-MM-DD')}`} className="font-mono text-[13px] text-[#2c6b7f] hover:underline">{t.orderId}</Link> },
    { key: 'customer', title: 'Khách', width: 170, render: (_, t) => customerOf.get(t.orderId) || '—' },
    { key: 'method', title: 'PTTT', width: 120, render: (_, t) => METHOD_LABEL[t.method] },
    { key: 'ref', title: 'Mã tham chiếu', width: 150, render: (_, t) => <span className="font-mono text-xs">{t.txnRef || '—'}</span> },
    { key: 'amount', title: 'Số tiền', align: 'right', width: 130, sorter: (a, b) => a.amount - b.amount, render: (_, t) => <b className={t.amount < 0 ? 'text-red-600' : ''}>{t.amount < 0 ? '-' : ''}{money(Math.abs(t.amount))}</b> },
    { key: 'cashier', title: 'Thu ngân', width: 110, dataIndex: 'cashier' },
    { key: 'branch', title: 'Chi nhánh', width: 140, render: (_, t) => branchName(t.branchId) },
  ];

  const doExport = () => exportExcel(`Giao_dich_${range[0].format('YYYYMMDD')}_${range[1].format('YYYYMMDD')}.xlsx`, 'Giao dịch', data.map(t => ({
    'Mã GD': t.id, 'Thời gian': fmtDateTime(t.at), 'Loại': KIND_LABEL[t.kind], 'Mã đơn': t.orderId, 'Khách': customerOf.get(t.orderId) || '',
    'Phương thức': METHOD_LABEL[t.method], 'Mã tham chiếu': t.txnRef, 'Số tiền': t.amount, 'Thu ngân': t.cashier, 'Chi nhánh': branchName(t.branchId),
  })));

  return (
    <PosShell
      title="Lịch sử giao dịch"
      subtitle="Giao dịch không sửa / xóa được (BR6.1). Bấm mã đơn để xem chi tiết."
      actions={<>
        {can('create') && <button onClick={() => setShiftOpen(true)} className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"><Calculator className="h-4 w-4" /> Chốt ca</button>}
        {can('export') && <button onClick={doExport} className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"><Download className="h-4 w-4" /> Xuất Excel</button>}
      </>}
    >
      <Tabs items={[
        {
          key: 'list', label: 'Giao dịch',
          children: (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <DatePicker.RangePicker value={range} format="DD/MM/YYYY" allowClear={false}
                  presets={[
                    { label: 'Hôm nay', value: [dayjs().startOf('day'), dayjs().endOf('day')] },
                    { label: '7 ngày', value: [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')] },
                    { label: 'Tháng này', value: [dayjs().startOf('month'), dayjs().endOf('day')] },
                  ]}
                  onChange={v => v && v[0] && v[1] && setRange([v[0].startOf('day'), v[1].endOf('day')])} />
                <Select mode="multiple" allowClear placeholder="Chi nhánh" className="min-w-[150px]" maxTagCount="responsive" value={branches} onChange={setBranches} options={branchOptions} disabled={role === 'cashier'} />
                <Select mode="multiple" allowClear placeholder="Phương thức" className="min-w-[140px]" maxTagCount="responsive" value={methods} onChange={setMethods} options={METHODS.map(m => ({ value: m, label: METHOD_LABEL[m] }))} />
                <Select mode="multiple" allowClear placeholder="Loại" className="min-w-[110px]" maxTagCount="responsive" value={kinds} onChange={setKinds} options={(Object.keys(KIND_LABEL) as TxnKind[]).map(k => ({ value: k, label: KIND_LABEL[k] }))} />
                <Select mode="multiple" allowClear placeholder="Thu ngân" className="min-w-[130px]" maxTagCount="responsive" value={cashiers} onChange={setCashiers} options={allCashiers.map(c => ({ value: c, label: c }))} />
                <Input.Search allowClear placeholder="Mã GD, mã đơn, mã tham chiếu, khách…" value={q} onChange={e => setQ(e.target.value)} className="w-72" />
              </div>
              <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                <div className="rounded-xl border border-[#3e91ab]/30 bg-[#ecf6f9] p-3">
                  <div className="text-xs font-semibold text-[#2c6b7f]">Thực thu</div>
                  <div className="text-xl font-extrabold text-[#2c6b7f]">{money(grand.income - grand.refund)}</div>
                  <div className="text-xs text-slate-500">Thu {money(grand.income)} · Hoàn <span className="text-red-600">-{money(grand.refund)}</span></div>
                </div>
                {summary.map(s => (
                  <div key={s.method} className="rounded-xl border border-slate-200 bg-white p-3">
                    <div className="text-xs font-semibold text-slate-500">{METHOD_LABEL[s.method]} · {s.count} GD</div>
                    <div className="text-lg font-bold text-slate-800">{money(s.net)}</div>
                    <div className="text-xs text-slate-500">Thu {money(s.income)}{s.refund > 0 && <> · <span className="text-red-600">Hoàn -{money(s.refund)}</span></>}</div>
                  </div>
                ))}
              </div>
              <Table<Transaction> rowKey="id" size="middle" className="custom-ant-table" columns={columns} dataSource={data} scroll={{ x: 'max-content' }}
                pagination={{ defaultPageSize: 20, showSizeChanger: true, showTotal: t => `${t} giao dịch` }} locale={{ emptyText: 'Không có giao dịch' }} />
            </>
          ),
        },
        {
          key: 'shifts', label: `Lịch sử chốt ca (${shiftCloses.length})`,
          children: (
            <Table rowKey="id" size="middle" dataSource={shiftCloses} pagination={{ pageSize: 20, hideOnSinglePage: true }} locale={{ emptyText: 'Chưa có lần chốt ca nào' }}
              columns={[
                { title: 'Mã', dataIndex: 'id', width: 90 },
                { title: 'Thời gian', width: 150, render: (_, s) => fmtDateTime(s.at) },
                { title: 'Chi nhánh', width: 140, render: (_, s) => branchName(s.branchId) },
                { title: 'Người chốt', dataIndex: 'by', width: 120 },
                { title: 'Tiền mặt hệ thống', align: 'right', render: (_, s) => money(s.expected.cash) },
                { title: 'Tiền mặt đếm', align: 'right', render: (_, s) => money(s.counted.cash) },
                { title: 'Chênh lệch', align: 'right', render: (_, s) => { const d = s.counted.cash - s.expected.cash; return <b className={d ? 'text-red-600' : 'text-green-700'}>{d > 0 ? '+' : ''}{money(d)}</b>; } },
                { title: 'Ghi chú', dataIndex: 'note' },
              ]} />
          ),
        },
      ]} />

      <ShiftCloseModal open={shiftOpen} onClose={() => setShiftOpen(false)} branchId={workingBranch} transactions={transactions}
        onSave={(expected, counted, note) => { closeShift({ branchId: workingBranch, at: new Date().toISOString(), by: userName, expected, counted, note }); setShiftOpen(false); message.success('Đã chốt ca'); }} />
    </PosShell>
  );
}

function ShiftCloseModal({ open, onClose, branchId, transactions, onSave }: {
  open: boolean; onClose: () => void; branchId: string; transactions: Transaction[];
  onSave: (expected: Record<PaymentMethod, number>, counted: Record<PaymentMethod, number>, note: string) => void;
}) {
  const expected = useMemo(() => {
    const e = emptyByMethod();
    transactions.filter(t => t.branchId === branchId && dayjs(t.at).isSame(dayjs(), 'day')).forEach(t => { e[t.method] += t.amount; });
    return e;
  }, [transactions, branchId]);
  const [counted, setCounted] = useState<Record<PaymentMethod, number>>(emptyByMethod());
  const [note, setNote] = useState('');
  const diffCash = counted.cash - expected.cash;

  return (
    <Modal open={open} onCancel={onClose} title={`Chốt ca hôm nay – ${BRANCHES.find(b => b.id === branchId)?.name}`} width={620} centered destroyOnHidden
      okText="Chốt ca" cancelText="Hủy" okButtonProps={{ disabled: diffCash !== 0 && note.trim().length < 5 }}
      onOk={() => onSave(expected, counted, note.trim())}
      afterOpenChange={o => { if (o) { setCounted({ ...emptyByMethod(), ...expected, cash: 0 }); setNote(''); } }}>
      <table className="w-full text-sm">
        <thead className="text-xs text-slate-500"><tr><th className="py-2 text-left">Phương thức</th><th className="py-2 text-right">Theo hệ thống</th><th className="py-2 text-right">Thực tế</th><th className="py-2 text-right">Chênh</th></tr></thead>
        <tbody>
          {METHODS.map(m => {
            const d = counted[m] - expected[m];
            return (
              <tr key={m} className="border-t border-slate-100">
                <td className="py-2 font-medium">{METHOD_LABEL[m]}</td>
                <td className="py-2 text-right">{money(expected[m])}</td>
                <td className="w-44 py-2 pl-3"><MoneyInput value={counted[m]} onChange={v => setCounted(c => ({ ...c, [m]: v }))} className="!h-9 !text-sm" /></td>
                <td className={`py-2 text-right font-semibold ${d ? 'text-red-600' : 'text-green-700'}`}>{d > 0 ? '+' : ''}{money(d)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="mt-3 text-xs text-slate-500">Nhập số tiền mặt đếm thực tế trong két. Có chênh lệch tiền mặt thì bắt buộc ghi chú lý do.</p>
      <Input.TextArea className="mt-2" rows={2} maxLength={300} placeholder="Ghi chú" value={note} onChange={e => setNote(e.target.value)} />
    </Modal>
  );
}
